import React, { Dispatch, SetStateAction } from 'react';
import { TreeViewProps } from '@mui/lab';
import { styled } from '@mui/material';
import { TreeItemProps } from '@mui/x-tree-view';
import { TreeViewBaseItem } from '@mui/x-tree-view/models';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';

import CheckboxTreeItem from './CheckboxTreeItem';

interface CheckboxTreeProps extends TreeViewProps<true> {
  items: TreeViewBaseItem[];
  selectedItemIds: string[];
  setSelectedItemIds: Dispatch<SetStateAction<string[]>>;
}

const CheckboxTree = ({
  items,
  selectedItemIds,
  setSelectedItemIds,
  ...props
}: CheckboxTreeProps) => {
  // 특정 아이템의 모든 하위 아이템 ID를 가져오는 함수
  const getAllChildrenIds = (item: TreeViewBaseItem): string[] => {
    let childrenIds: string[] = [item.id as string];
    if (item.children) {
      item.children.forEach((child: TreeViewBaseItem) => {
        childrenIds = [...childrenIds, ...getAllChildrenIds(child)];
      });
    }
    return childrenIds;
  };

  // 아이템의 모든 하위 아이템이 선택되었는지 확인하는 함수
  const areAllChildrenSelected = (item: TreeViewBaseItem): boolean => {
    const childrenIds = getAllChildrenIds(item);
    return childrenIds.every((id) => selectedItemIds.includes(id));
  };

  // 선택된 아이템 토글 핸들러
  const handleItemSelectionToggle = (id: string) => {
    const item = findItemById(items, id);
    if (!item) return;

    const isSelected = selectedItemIds.includes(id);
    let newSelectedIds: string[];

    if (isSelected) {
      // 체크 해제 시: 현재 아이템과 모든 하위 아이템 해제
      const childrenIds = getAllChildrenIds(item);
      newSelectedIds = selectedItemIds.filter((sid) => !childrenIds.includes(sid));
    } else {
      // 체크 시: 현재 아이템과 모든 하위 아이템 선택
      const childrenIds = getAllChildrenIds(item);
      newSelectedIds = Array.from(new Set([...selectedItemIds, ...childrenIds]));
    }

    setSelectedItemIds(newSelectedIds);
  };

  // ID로 아이템을 찾는 헬퍼 함수
  const findItemById = (items: TreeViewBaseItem[], id: string): TreeViewBaseItem | null => {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children) {
        const found = findItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  return (
    <StyledTreeView
      checkboxSelection
      items={items}
      selectedItems={selectedItemIds}
      onSelectedItemsChange={(_, nodeIds) => setSelectedItemIds(nodeIds as string[])}
      slots={{
        item: CheckboxTreeItem as unknown as React.ComponentType<TreeItemProps>,
      }}
      slotProps={{
        item: (props) => {
          const item = findItemById(items, props.itemId);
          return {
            isSelect: selectedItemIds.includes(props.itemId),
            isIndeterminate: item?.children?.length
              ? !areAllChildrenSelected(item) &&
                item.children.some((child) => selectedItemIds.includes(child.id as string))
              : false,
            onToggle: handleItemSelectionToggle,
          } as any;
        },
      }}
      {...props}
    />
  );
};

export default CheckboxTree;

const StyledTreeView = styled(RichTreeView)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(1),
}));
